import React, { useState, useRef, useEffect, lazy, Suspense } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import LandingPage from './components/LandingPage';
import Login from './components/Login';
import Register from './components/Register';
import WorkoutBuilder from './components/WorkoutBuilder';
import LineupManager from './components/LineupManager';
import DailyWorkout from './components/DailyWorkout';
import AttendanceTab from './components/AttendanceTab';
import UserProfile from './components/UserProfile';
import BoatManagement from './components/BoatManagement';
import TeamSettings from './components/TeamSettings';
import TeamAnnouncement from './components/TeamAnnouncement';
import ClubManagement from './components/ClubManagement';
import CoachTeamManagement from './components/CoachTeamManagement';
import TrainingLog from './components/TrainingLog';
import AthleteResults from './components/AthleteResults';
import ResetPassword from './components/ResetPassword';
import PrivacyPolicy from './components/PrivacyPolicy';
import EULA from './components/EULA';
import TermsAndConditions from './components/TermsAndConditions';
import ClubSwitcher from './components/ClubSwitcher';
import ClubMembershipSwitcher from './components/ClubMembershipSwitcher';
import TeamSwitcher from './components/TeamSwitcher';
import TeamRoster from './components/TeamRoster';
import InternalReports from './components/InternalReports';
import Reports from './components/Reports';
// Chart-heavy analytics dashboards are code-split so Recharts stays out of
// the initial bundle.
const AthleteAnalytics = lazy(() => import('./components/analytics/AthleteAnalytics'));
const CoachAnalytics = lazy(() => import('./components/analytics/CoachAnalytics'));
// Coach-only, feature-flagged tool; code-split to keep it out of the initial bundle.
const TrainingPlanBuilder = lazy(() => import('./components/TrainingPlanBuilder'));

const AnalyticsFallback = () => (
  <div className="flex items-center justify-center h-64 text-theme-primary">Loading analytics...</div>
);

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// If Strava was configured to return to the website instead of the API,
// the browser lands here with ?code=&state= and nothing ever writes
// strava_connections. Hand those params to the backend callback.
if (typeof window !== 'undefined') {
  const oauthParams = new URLSearchParams(window.location.search);
  if (oauthParams.get('code') && oauthParams.get('state') && !oauthParams.get('strava')) {
    const callback = new URL(`${API_URL.replace(/\/$/, '')}/api/strava/callback`);
    oauthParams.forEach((value, key) => callback.searchParams.set(key, value));
    window.location.replace(callback.toString());
  }
}

// Hamburger Menu Icon
const MenuIcon = ({ isOpen }) => (
  <svg
    className="w-6 h-6"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    {isOpen ? (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M6 18L18 6M6 6l12 12"
      />
    ) : (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 6h16M4 12h16M4 18h16"
      />
    )}
  </svg>
);

// User/Account Icon
const UserIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
    />
  </svg>
);

// Chevron Icon for dropdown
const ChevronIcon = ({ isOpen }) => (
  <svg
    className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 9l-7 7-7-7"
    />
  </svg>
);

// Navigation icons for menu items
const NavIcons = {
  workouts: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  attendance: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
    </svg>
  ),
  boats: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15l9-9 9 9M12 6v15" />
    </svg>
  ),
  coach: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  ),
  announce: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
    </svg>
  ),
  settings: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  profile: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  ),
  teams: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  ),
  logout: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
    </svg>
  ),
  training: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h-.01M6.343 6.343l5.657 5.657m-7.05 7.05l5.657-5.657M17.657 6.343l-5.657 5.657M6.343 17.657l5.657-5.657" />
    </svg>
  ),
  reports: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-6a3 3 0 016 0v6m-8 4h10M5 3h14a2 2 0 012 2v12a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2z" />
    </svg>
  ),
  results: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  ),
  analytics: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3v18h18M7 15l3-3 3 3 5-6" />
    </svg>
  ),
  roster: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  ),
  lineups: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h10M4 14h16M4 18h10" />
    </svg>
  ),
  boatReports: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  damageReports: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  ),
};

function MainApp() {
  const { user, loading, statusMessage, logout, isCoach, isAdmin, isSuperAdmin } = useAuth();
  const { teamSettings } = useTheme();
  
  // Read the tab from the URL on the first render. If this stays null until an
  // effect, a later "default tab" effect sees tab === null and overwrites
  // ?tab=profile (Strava OAuth return) with Daily Workout.
  const [tab, setTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const strava = params.get('strava');
    if (strava) {
      try {
        sessionStorage.setItem('stravaOAuthResult', strava);
      } catch {
        // private mode
      }
      return 'profile';
    }
    return params.get('tab');
  });
  const [showRegister, setShowRegister] = useState(false);
  const [showLogin, setShowLogin] = useState(false); // For navigating from landing to login
  const [menuOpen, setMenuOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [coachMenuExpanded, setCoachMenuExpanded] = useState(false);
  const [urlTemplateId, setUrlTemplateId] = useState(null); // Template ID from URL
  
  const menuRef = useRef(null);
  const accountMenuRef = useRef(null);

  // Check URL for template parameter (from email links or universal links)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const stravaParam = urlParams.get('strava');
    const tabParam = urlParams.get('tab');
    if (stravaParam) {
      try {
        sessionStorage.setItem('stravaOAuthResult', stravaParam);
      } catch {
        // private mode
      }
      setTab('profile');
    } else if (tabParam) {
      setTab(tabParam);
    }
    // Support both 'template' (web emails) and 'templateId' (universal links)
    const templateId = urlParams.get('template') || urlParams.get('templateId');
    const currentPath = window.location.pathname;
    
    if (templateId && (currentPath === '/results' || currentPath === '/')) {
      console.log('[App] Found template ID in URL:', templateId);
      setUrlTemplateId(parseInt(templateId));
      // Navigate to results tab
      setTab('athleteResults');
      // Clean up URL without reload
      window.history.replaceState({}, '', '/');
    }
  }, []);

  // Set default tab once user loads (only if not already set by URL param)
  // Priority: Daily Workout if user has team, otherwise Boats
  useEffect(() => {
    if (user && tab === null) {
      setTab(user.team ? 'workouts' : 'boats');
    }
  }, [user, tab]);

  // Reset tab if user doesn't have team and is on a team-specific tab
  useEffect(() => {
    if (user && !user.team && ['workouts', 'attendance', 'roster'].includes(tab)) {
      setTab('boats');
    }
  }, [user, tab]);

  // Update favicon and page title based on team settings
  useEffect(() => {
    // Update page title to club name
    if (teamSettings?.displayName) {
      document.title = teamSettings.displayName;
    } else {
      document.title = 'Team Central';
    }

    // Update favicon if logo URL is provided
    if (teamSettings?.logoUrl) {
      const favicon = document.getElementById('favicon');
      if (favicon) {
        favicon.href = teamSettings.logoUrl;
        // Update type based on file extension
        if (teamSettings.logoUrl.endsWith('.svg')) {
          favicon.type = 'image/svg+xml';
        } else if (teamSettings.logoUrl.endsWith('.png')) {
          favicon.type = 'image/png';
        } else if (teamSettings.logoUrl.endsWith('.jpg') || teamSettings.logoUrl.endsWith('.jpeg')) {
          favicon.type = 'image/jpeg';
        }
      }
    }
  }, [teamSettings?.displayName, teamSettings?.logoUrl]);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target)) {
        setAccountMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close menus on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        setAccountMenuOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  // Check for public pages first (accessible regardless of auth status)
  const pathname = window.location.pathname;
  if (pathname === '/privacy') {
    return <PrivacyPolicy />;
  }
  if (pathname === '/eula') {
    return <EULA />;
  }
  if (pathname === '/terms') {
    return <TermsAndConditions />;
  }

  if (loading || (user && tab === null)) {
    return (
      <div className="min-h-screen bg-theme-bg flex items-center justify-center">
        <div className="text-2xl text-theme-primary">{statusMessage || 'Loading...'}</div>
      </div>
    );
  }

  if (!user) {

    // Check URL for special pages
    const urlParams = new URLSearchParams(window.location.search);
    const currentPath = window.location.pathname;
    
    // Check if on password reset page
    const resetToken = urlParams.get('token');
    const isResetPage = currentPath === '/reset-password' || resetToken;

    if (isResetPage && resetToken) {
      return <ResetPassword token={resetToken} onSuccess={() => window.location.href = '/'} />;
    }

    // Check if on registration page (via invite link)
    const inviteToken = urlParams.get('invite');
    const isRegisterPage = currentPath === '/register' || inviteToken;

    if (isRegisterPage) {
      return (
        <Register 
          onSwitchToLogin={() => window.location.href = '/'} 
          onBackToLanding={() => window.location.href = '/'}
        />
      );
    }

    // Show landing page by default, login/register when requested
    if (!showLogin && !showRegister) {
      return (
        <LandingPage 
          onLogin={() => setShowLogin(true)} 
        />
      );
    }

    return showRegister ? (
      <Register 
        onSwitchToLogin={() => { setShowRegister(false); setShowLogin(true); }} 
        onBackToLanding={() => { setShowLogin(false); setShowRegister(false); }}
      />
    ) : (
      <Login 
        onSwitchToRegister={() => { setShowLogin(false); setShowRegister(true); }} 
        onBackToLanding={() => { setShowLogin(false); setShowRegister(false); }}
      />
    );
  }

  // Check if user has a team assigned
  const hasTeam = user?.team;

  // Regular tabs visible to all users
  const mainTabs = [];

  // Team-specific tabs only show if user has a team assigned
  if (hasTeam) {
    mainTabs.push({ id: 'workouts', label: 'Daily Workout', mobileLabel: 'Today', icon: NavIcons.workouts });
    mainTabs.push({ id: 'attendance', label: 'Attendance', mobileLabel: 'Absence', icon: NavIcons.attendance });
    mainTabs.push({ id: 'roster', label: 'Roster', mobileLabel: 'Roster', icon: NavIcons.roster });
  }

  mainTabs.push({ id: 'trainingLog', label: 'Training Log', mobileLabel: 'Training', icon: NavIcons.training });

  // Equipment (boats/ergs) is club-wide, always visible
  mainTabs.push({ id: 'boats', label: 'Equipment', mobileLabel: 'Equip', icon: NavIcons.boats });

  // Athlete Results - available to all users (athletes can log their own)
  mainTabs.push({ id: 'athleteResults', label: 'My Results', mobileLabel: 'Results', icon: NavIcons.results });

  // Personal analytics dashboard - available to all users
  mainTabs.push({ id: 'myAnalytics', label: 'My Progress', mobileLabel: 'Progress', icon: NavIcons.analytics });

  // Coach/Admin tabs (shown in collapsible section)
  const coachTabs = [];
  
  if (isCoach() || isAdmin()) {
    if (hasTeam) {
      coachTabs.push({ id: 'coach', label: 'Workout Builder', icon: NavIcons.coach });
      coachTabs.push({ id: 'lineups', label: 'Lineups', icon: NavIcons.lineups });
      coachTabs.push({ id: 'announce', label: 'Announce', icon: NavIcons.announce });
    }
    // Feature-flagged per team; admins always see it so they can review any team's plan.
    if (teamSettings?.trainingPlanEnabled || isAdmin()) {
      coachTabs.push({ id: 'trainingPlan', label: 'Training Plan', icon: NavIcons.workouts });
    }
    coachTabs.push({ id: 'reports', label: 'Template Builder', icon: NavIcons.reports });
    coachTabs.push({ id: 'internalReports', label: 'Internal Reports', icon: NavIcons.reports });
    coachTabs.push({ id: 'coachAnalytics', label: 'Team Analytics', icon: NavIcons.analytics });
  }

  // Check if current tab is a coach tab (to keep section expanded)
  const isCoachTabActive = coachTabs.some(t => t.id === tab);

  const hasTeamMembership = Boolean(user?.team) || (Array.isArray(user?.teams) && user.teams.length > 0);

  const accountMenuItems = [
    { id: 'profile', label: 'My Profile', icon: NavIcons.profile },
  ];

  if ((isCoach() || isAdmin()) && hasTeamMembership) {
    accountMenuItems.push({ id: 'teamManagement', label: 'Team Management', icon: NavIcons.teams });
  }

  if (isAdmin()) {
    accountMenuItems.push({ id: 'clubManagement', label: 'Club Management', icon: NavIcons.teams });
    accountMenuItems.push({ id: 'settings', label: 'Club Settings', icon: NavIcons.settings });
  }

  const handleNavClick = (tabId) => {
    setTab(tabId);
    setMenuOpen(false);
    setAccountMenuOpen(false);
  };

  const handleLogout = () => {
    setAccountMenuOpen(false);
    logout();
  };

  // Build bottom tab bar items for mobile
  // Priority order matches mobile app: Today, Attendance, Training, Results, Equipment
  // Note: Only show team-specific tabs (workouts, attendance) if user has a team
  const mobilePriorityTabs = hasTeam 
    ? ['workouts', 'attendance', 'trainingLog', 'athleteResults', 'boats']
    : ['trainingLog', 'athleteResults', 'boats'];
  const mobileMainTabs = mobilePriorityTabs
    .map(id => mainTabs.find(t => t.id === id))
    .filter(Boolean); // Only include tabs that exist in mainTabs
  
  // Regular items for "More" menu (excluding coach tabs)
  const mobileMoreItems = [
    ...mainTabs.filter(t => !mobilePriorityTabs.includes(t.id)),
    ...accountMenuItems
  ];
  const showMoreTab = mobileMoreItems.length > 0 || coachTabs.length > 0;

  return (
    <div className="min-h-dvh bg-theme-bg pb-mobile-tabbar sm:min-h-screen sm:pb-0">
      {/* Fixed Navigation Header */}
      <header className="sticky top-0 z-50 bg-theme-primary shadow-lg safe-area-top">
        <div className="max-w-7xl mx-auto safe-area-x">
          <div className="flex justify-between items-center h-14 sm:h-16">
            {/* Left side: Hamburger Menu (desktop only) */}
            <div className="flex items-center gap-4">
              <div className="relative hidden sm:block" ref={menuRef}>
                <button
                  onClick={() => setMenuOpen(!menuOpen)}
                  className="p-2 rounded-lg bg-theme-secondary text-theme-primary-dark hover:brightness-110 transition-all focus:outline-none focus:ring-2 focus:ring-white/40"
                  aria-label="Toggle navigation menu"
                  aria-expanded={menuOpen}
                >
                  <MenuIcon isOpen={menuOpen} />
                </button>

                {/* Navigation Dropdown */}
                <div
                  className={`absolute left-0 mt-2 w-64 bg-white rounded-xl shadow-2xl overflow-hidden transform transition-all duration-200 origin-top-left border border-gray-200 ${
                    menuOpen
                      ? 'opacity-100 scale-100 translate-y-0'
                      : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'
                  }`}
                >
                  <nav className="py-2">
                    {/* Regular tabs */}
                    {mainTabs.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => handleNavClick(item.id)}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                          tab === item.id
                            ? 'bg-theme-primary text-white font-semibold'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <span className={tab === item.id ? 'text-white' : 'text-theme-primary'}>
                          {item.icon}
                        </span>
                        <span>{item.label}</span>
                        {tab === item.id && (
                          <span className="ml-auto w-2 h-2 rounded-full bg-white"></span>
                        )}
                      </button>
                    ))}

                    {/* Coach/Admin Section */}
                    {coachTabs.length > 0 && (
                      <>
                        <div className="border-t border-gray-200 my-2"></div>
                        <button
                          onClick={() => setCoachMenuExpanded(!coachMenuExpanded)}
                          className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                            isCoachTabActive
                              ? 'bg-theme-primary/10 text-theme-primary font-semibold'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          <span className="text-theme-primary">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                            </svg>
                          </span>
                          <span>Coach Tools</span>
                          {isCoachTabActive && (
                            <span className="ml-auto w-2 h-2 rounded-full bg-theme-primary"></span>
                          )}
                          <svg 
                            className={`w-4 h-4 ml-auto transition-transform ${coachMenuExpanded || isCoachTabActive ? 'rotate-180' : ''}`} 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>

                        {/* Expandable coach tabs */}
                        <div className={`overflow-hidden transition-all duration-200 ${
                          coachMenuExpanded || isCoachTabActive ? 'max-h-96' : 'max-h-0'
                        }`}>
                          {coachTabs.map((item) => (
                            <button
                              key={item.id}
                              onClick={() => handleNavClick(item.id)}
                              className={`w-full flex items-center gap-3 pl-8 pr-4 py-2.5 text-left transition-colors text-sm ${
                                tab === item.id
                                  ? 'bg-theme-primary text-white font-semibold'
                                  : 'text-gray-600 hover:bg-gray-100'
                              }`}
                            >
                              <span className={tab === item.id ? 'text-white' : 'text-theme-primary'}>
                                {item.icon}
                              </span>
                              <span>{item.label}</span>
                              {tab === item.id && (
                                <span className="ml-auto w-2 h-2 rounded-full bg-white"></span>
                              )}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </nav>
                </div>
              </div>

              {/* Team Logo & Name */}
              <div className="flex items-center gap-2 sm:gap-3">
                {teamSettings?.logoUrl && (
                  <img 
                    src={teamSettings.logoUrl} 
                    alt={`${teamSettings?.displayName || 'Team'} logo`}
                    className="h-8 sm:h-10 w-auto object-contain"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
                <h1 className="text-lg sm:text-2xl font-bold text-white tracking-wide truncate max-w-[140px] sm:max-w-none">
                  {teamSettings?.displayName || 'Team Central'}
                </h1>
              </div>
            </div>

            {/* Superadmin Club Switcher — all-clubs list is superadmin-only */}
            {isSuperAdmin() && <ClubSwitcher />}

            {/* Multi-club membership switcher */}
            <ClubMembershipSwitcher onClubSwitch={() => window.location.reload()} />

            {/* Multi-team switcher */}
            <TeamSwitcher onTeamSwitch={() => window.location.reload()} />

            {/* Right side: Account Menu */}
            <div className="relative" ref={accountMenuRef}>
              <button
                onClick={() => setAccountMenuOpen(!accountMenuOpen)}
                className="flex items-center gap-2 px-2 sm:px-3 py-2 rounded-lg bg-theme-secondary text-theme-primary-dark hover:brightness-110 transition-all focus:outline-none focus:ring-2 focus:ring-white/40"
                aria-label="Account menu"
                aria-expanded={accountMenuOpen}
              >
                <div className="w-8 h-8 rounded-full bg-theme-primary/20 flex items-center justify-center">
                  <UserIcon />
                </div>
                <div className="hidden sm:block text-left">
                  <div className="text-sm font-medium">{user.name}</div>
                  <div className="text-xs text-theme-primary/70 capitalize">{user.role}</div>
                </div>
                <ChevronIcon isOpen={accountMenuOpen} />
              </button>

              {/* Account Dropdown */}
              <div
                className={`absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-2xl overflow-hidden transform transition-all duration-200 origin-top-right border border-gray-200 ${
                  accountMenuOpen
                    ? 'opacity-100 scale-100 translate-y-0'
                    : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'
                }`}
              >
                {/* User Info Header */}
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                  <div className="font-medium text-gray-900">{user.name}</div>
                  <div className="text-sm text-gray-500 capitalize">{user.role}</div>
                </div>

                {/* Menu Items */}
                <nav className="py-2">
                  {accountMenuItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => handleNavClick(item.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                        tab === item.id
                          ? 'bg-theme-primary text-white font-semibold'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <span className={tab === item.id ? 'text-white' : 'text-theme-primary'}>
                        {item.icon}
                      </span>
                      <span>{item.label}</span>
                    </button>
                  ))}

                  {/* Divider */}
                  <div className="my-2 border-t border-gray-200"></div>

                  {/* Logout */}
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <span>{NavIcons.logout}</span>
                    <span>Logout</span>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* No Team Warning Banner */}
      {!hasTeam && (
        <div className="bg-amber-100 border-b border-amber-300 py-2 sm:py-3">
          <div className="max-w-7xl mx-auto safe-area-x flex items-center gap-2">
            <svg className="w-5 h-5 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span className="text-amber-800 text-xs sm:text-sm">
              <strong>Not assigned to a team.</strong> <span className="hidden sm:inline">Contact your admin to be assigned to a team to access workouts, attendance, and roster.</span>
            </span>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto safe-area-x py-4 sm:py-6">
        <div className="bg-white shadow-xl rounded-xl sm:rounded-2xl p-3 sm:p-6 w-full min-w-0">
          {tab === 'workouts' && hasTeam && <DailyWorkout />}
          {tab === 'attendance' && hasTeam && <AttendanceTab />}
          {tab === 'roster' && hasTeam && <TeamRoster />}
          {tab === 'coach' && <WorkoutBuilder />}
          {tab === 'lineups' && (isCoach() || isAdmin()) && hasTeam && <LineupManager />}
          {tab === 'boats' && <BoatManagement token={localStorage.getItem('token')} />}
          {tab === 'profile' && <UserProfile />}
          {tab === 'announce' && <TeamAnnouncement />}
          {tab === 'teamManagement' && (isCoach() || isAdmin()) && hasTeamMembership && <CoachTeamManagement />}
          {tab === 'clubManagement' && <ClubManagement />}
          {tab === 'settings' && <TeamSettings />}
          {tab === 'trainingLog' && <TrainingLog />}
          {tab === 'reports' && <Reports />}
          {tab === 'internalReports' && <InternalReports token={localStorage.getItem('token')} />}
          {tab === 'athleteResults' && <AthleteResults urlTemplateId={urlTemplateId} onTemplateHandled={() => setUrlTemplateId(null)} />}
          {tab === 'myAnalytics' && (
            <Suspense fallback={<AnalyticsFallback />}>
              <AthleteAnalytics />
            </Suspense>
          )}
          {tab === 'coachAnalytics' && (isCoach() || isAdmin()) && (
            <Suspense fallback={<AnalyticsFallback />}>
              <CoachAnalytics />
            </Suspense>
          )}
          {tab === 'trainingPlan' && (isCoach() || isAdmin()) && (
            <Suspense fallback={<AnalyticsFallback />}>
              <TrainingPlanBuilder />
            </Suspense>
          )}
        </div>
      </main>

      {/* Bottom Tab Bar (Mobile Only) */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 sm:hidden safe-area-bottom"
        aria-label="Primary"
      >
        <div className="flex justify-around items-center h-16 min-h-[3.5rem]">
          {mobileMainTabs.map((item) => (
            <button
              key={item.id}
              onClick={() => handleNavClick(item.id)}
              className={`flex flex-col items-center justify-center flex-1 h-full py-1 transition-colors ${
                tab === item.id
                  ? 'text-theme-primary'
                  : 'text-gray-500'
              }`}
            >
              <span className={`${tab === item.id ? 'scale-110' : ''} transition-transform`}>
                {item.icon}
              </span>
              <span className={`text-[10px] mt-1 ${tab === item.id ? 'font-semibold' : ''}`}>
                {item.mobileLabel || item.label.split(' ')[0]}
              </span>
              {tab === item.id && (
                <span className="absolute bottom-1 w-1 h-1 rounded-full bg-theme-primary"></span>
              )}
            </button>
          ))}
          
          {/* More button for additional tabs */}
          {showMoreTab && (
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className={`flex flex-col items-center justify-center flex-1 h-full py-1 transition-colors ${
                menuOpen || mobileMoreItems.some(item => item.id === tab) || coachTabs.some(item => item.id === tab)
                  ? 'text-theme-primary'
                  : 'text-gray-500'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              <span className="text-[10px] mt-1">More</span>
            </button>
          )}
        </div>
      </nav>

      {/* Mobile "More" Menu Sheet */}
      {menuOpen && (
        <div className="fixed inset-0 z-[60] sm:hidden">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 z-0"
            onClick={() => setMenuOpen(false)}
          />
          
          {/* Bottom Sheet - extends to bottom, with padding for tab bar */}
          <div 
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl animate-slide-up z-10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-10 h-1 bg-gray-300 rounded-full"></div>
            </div>
            <nav className="px-4 max-sm:pb-[max(5.5rem,env(safe-area-inset-bottom,0px)+3rem)] pb-6 max-h-[min(70vh,100dvh)] overflow-y-auto overscroll-contain">
              {/* Remaining main tabs (non-coach) */}
              {mainTabs.filter(t => !mobilePriorityTabs.includes(t.id)).length > 0 && (
                <>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">More Options</p>
                  {mainTabs.filter(t => !mobilePriorityTabs.includes(t.id)).map((item) => (
                    <button
                      type="button"
                      key={item.id}
                      onPointerDown={(e) => {
                        e.stopPropagation();
                        setTab(item.id);
                        setMenuOpen(false);
                      }}
                      className={`w-full flex items-center gap-4 px-4 py-4 rounded-xl mb-1 transition-colors active:bg-gray-200 ${
                        tab === item.id
                          ? 'bg-theme-primary text-white'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <span className={`pointer-events-none ${tab === item.id ? 'text-white' : 'text-theme-primary'}`}>
                        {item.icon}
                      </span>
                      <span className="font-medium pointer-events-none">{item.label}</span>
                    </button>
                  ))}
                </>
              )}

              {/* Coach Tools Section */}
              {coachTabs.length > 0 && (
                <>
                  <div className="border-t border-gray-200 my-4"></div>
                  <button
                    type="button"
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      setCoachMenuExpanded(!coachMenuExpanded);
                    }}
                    className={`w-full flex items-center gap-4 px-4 py-4 rounded-xl mb-1 transition-colors active:bg-gray-200 ${
                      isCoachTabActive
                        ? 'bg-theme-primary/10 text-theme-primary'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <span className="text-theme-primary pointer-events-none">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </span>
                    <span className="font-semibold pointer-events-none">Coach Tools</span>
                    <svg 
                      className={`w-4 h-4 ml-auto transition-transform pointer-events-none ${coachMenuExpanded || isCoachTabActive ? 'rotate-180' : ''}`} 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Expandable coach tabs */}
                  <div className={`overflow-hidden transition-all duration-200 ${
                    coachMenuExpanded || isCoachTabActive ? 'max-h-96' : 'max-h-0'
                  }`}>
                    {coachTabs.map((item) => (
                      <button
                        type="button"
                        key={item.id}
                        onPointerDown={(e) => {
                          e.stopPropagation();
                          setTab(item.id);
                          setMenuOpen(false);
                        }}
                        className={`w-full flex items-center gap-4 pl-12 pr-4 py-3 rounded-xl mb-1 transition-colors active:bg-gray-200 ${
                          tab === item.id
                            ? 'bg-theme-primary text-white'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <span className={`pointer-events-none ${tab === item.id ? 'text-white' : 'text-theme-primary'}`}>
                          {item.icon}
                        </span>
                        <span className="font-medium pointer-events-none">{item.label}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
              
              {/* Account items */}
              {accountMenuItems.length > 0 && (
                <>
                  <div className="border-t border-gray-200 my-4"></div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Account</p>
                  {accountMenuItems.map((item) => (
                    <button
                      type="button"
                      key={item.id}
                      onPointerDown={(e) => {
                        e.stopPropagation();
                        setTab(item.id);
                        setMenuOpen(false);
                      }}
                      className={`w-full flex items-center gap-4 px-4 py-4 rounded-xl mb-1 transition-colors active:bg-gray-200 ${
                        tab === item.id
                          ? 'bg-theme-primary text-white'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <span className={`pointer-events-none ${tab === item.id ? 'text-white' : 'text-theme-primary'}`}>
                        {item.icon}
                      </span>
                      <span className="font-medium pointer-events-none">{item.label}</span>
                    </button>
                  ))}
                </>
              )}
              
              {/* Logout */}
              <div className="border-t border-gray-200 my-4"></div>
              <button
                type="button"
                onPointerDown={(e) => {
                  e.stopPropagation();
                  setMenuOpen(false);
                  logout();
                }}
                className="w-full flex items-center gap-4 px-4 py-4 rounded-xl text-red-600 hover:bg-red-50 transition-colors active:bg-red-100"
              >
                <span className="pointer-events-none">{NavIcons.logout}</span>
                <span className="font-medium pointer-events-none">Logout</span>
              </button>
            </nav>
          </div>
        </div>
      )}

      {/* Overlay for desktop menu */}
      {(menuOpen || accountMenuOpen) && (
        <div 
          className="fixed inset-0 bg-black/20 z-40 hidden sm:block"
          onClick={() => {
            setMenuOpen(false);
            setAccountMenuOpen(false);
          }}
        />
      )}
    </div>
  );
}

// Wrapper component to connect AuthProvider with ThemeProvider
function AppWithProviders() {
  const { updateTeamSettings, fetchTeamSettings } = useTheme();

  return (
    <AuthProvider
      onTeamSettingsUpdate={updateTeamSettings}
      fetchTeamSettings={fetchTeamSettings}
    >
      <MainApp />
    </AuthProvider>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AppWithProviders />
    </ThemeProvider>
  );
}
